/**
 * How people talk, by country. Lagos speaks Pidgin and Lagos English; everyone else speaks their own way
 * (in English, with the odd local word glossed). Strangers talk like where you are; your friends and
 * family talk like where you're from.
 */
import type { Country } from './travel.ts';

export interface Voice {
  /** someone you asked to take your picture: yes / no */
  photoYes: string[];
  photoNo: string[];
  /** a friend texting out of the blue */
  friendText: string[];
  /** comments under your posts */
  commentFamily: string[];
  commentFriend: string[];
  commentStranger: string[];
  commentFlat: string[];
  /** someone you bump into on the pavement */
  bump: string[];
  /** a driver letting you out */
  dropoff: string;
  /** a friend over a drink: the boss line, the throwaway advice, the brush-off, and the shared memory */
  boss: string;
  quit: string;
  shrug: string;
  memory: [string, string];
  /** turning down a date */
  friendsOnly: string;
}

export const VOICES: Record<Country, Voice> = {
  nigeria: {
    photoYes: ['"Okay, no problem. Stand there."', '"Ehen, give me the phone. Smile o."', '"Sure. Should I get the whole building?"', '"Alright. One, two…"'],
    photoNo: ['"Abeg, I\'m in a hurry."', '"No, I can\'t hold your phone. Sorry."', 'They pretend not to hear you.', '"Ask someone else."'],
    friendText: ['Where you dey? Long time.', 'Una go come out this weekend?', 'I saw something you would like.'],
    commentFamily: ['God bless you my child 🙏', 'Fine boy/girl! Where is this?', 'Call me when you see this.', 'You look like your father here 😂', 'Have you eaten?'],
    commentFriend: ['Who snap you? 🔥', 'Clean!!', 'Omo see vibes 😍', 'Abeg where is this place?', 'You dey enjoy o', 'Drip too much 💧', 'Send me this pic'],
    commentStranger: ['Nice one 👌', 'Lagos no dey carry last 🔥', 'This light is everything', 'Follow back pls', 'Where is this?', 'Big vibes', '❤️❤️'],
    commentFlat: ['Nice', '👍', 'Ok o'],
    bump: ['Ehn! Watch where you dey go!', 'Oga, easy!', 'Excuse me o!', 'Abeg, look front!', 'Can\'t you see?', 'Hey! Watch it!', 'Na wa o.', 'Shey you no get eye?', 'Sorry o… for yourself.', 'Careful!'],
    dropoff: '"Thank you o. Rate me five stars!"',
    boss: '"Work has been crazy this week. My oga is on my neck."',
    quit: '"Just quit, abeg."',
    shrug: '"Ehn, she will be fine."',
    memory: ['"Remember that night in Yaba when the light went off at the party?"', '"And you fell into the pepper soup!"'],
    friendsOnly: '"Let\'s just stay friends, abeg."',
  },
  france: {
    photoYes: ['"Bien sûr. Stand there, with the street behind you."', '"Oui, oui. Smile… parfait."', '"Okay. Portrait or landscape?"', '"Un, deux, trois…"'],
    photoNo: ['"Désolé, I\'m in a hurry."', '"Non, merci."', 'They pretend not to hear you.', '"Ask someone else, sorry."'],
    friendText: ['Ça va? Haven\'t seen you in ages.', 'Apéro this weekend?', 'I saw something you\'d like.'],
    commentFamily: ['Très beau! Bisous ❤️', 'Where is this? Call your mother.', 'You look like your father here 😂', 'Are you eating properly?'],
    commentFriend: ['Trop stylé 🔥', 'Magnifique!!', 'Where is this?? 😍', 'La classe', 'Send me this pic', 'Tu me manques'],
    commentStranger: ['Superbe 👌', 'Paris ❤️', 'This light is everything', 'Follow back?', 'Where is this?', '❤️❤️'],
    commentFlat: ['Sympa', '👍', 'Ok'],
    bump: ['Pardon!', 'Hé! Faites attention!', 'Oh là là…', 'Excusez-moi!', 'Careful!', 'Mais regardez où vous allez!'],
    dropoff: '"Voilà. Bonne journée!"',
    boss: '"Work has been crazy this week. My boss is impossible."',
    quit: '"Just quit. Life is short."',
    shrug: '"Bof, she\'ll be fine."',
    memory: ['"Remember the night the Métro stopped and we walked home across Paris?"', '"And you lost a shoe on the Pont Neuf!"'],
    friendsOnly: '"Let\'s just stay friends, d\'accord?"',
  },
  uae: {
    photoYes: ['"Sure! Stand there, with the tower behind you."', '"Of course, habibi. Smile…"', '"Vertical? The tower won’t fit otherwise."', '"One, two, three…"'],
    photoNo: ['"Sorry, I’m in a rush."', '"No, thank you."', 'They pretend not to hear you.', '"Ask someone else, sorry."'],
    friendText: ['Habibi! Haven’t seen you in ages.', 'Brunch this Friday?', 'I saw something you’d like.'],
    commentFamily: ['Mashallah! ❤️', 'Where is this? Call your mother.', 'You look like your father here 😂', 'Are you eating properly?'],
    commentFriend: ['Fire 🔥', 'Mashallah!!', 'Where is this?? 😍', 'Living the life', 'Send me this pic', 'Miss you'],
    commentStranger: ['Beautiful 👌', 'Dubai ❤️', 'This view is everything', 'Follow back?', 'Where is this?', '❤️❤️'],
    commentFlat: ['Nice', '👍', 'Ok'],
    bump: ['Sorry!', 'Hey! Careful!', 'Yalla, move!', 'Excuse me!', 'Watch it!', 'Habibi, look where you’re going!'],
    dropoff: '"Here we are. Have a good day!"',
    boss: '"Work has been crazy this week. My manager is impossible."',
    quit: '"Just quit. Life is short."',
    shrug: '"Maalesh, she’ll be fine."',
    memory: ['"Remember the night we drove out to the desert and watched the stars till the sun came up?"', '"And the car got stuck in the sand!"'],
    friendsOnly: '"Let’s stay friends, okay?"',
  },
  italy: {
    photoYes: ['"Certo! Stand there, with the piazza behind you."', '"Sì, sì. Smile… perfetto."', '"Okay. Vertical or horizontal?"', '"Uno, due, tre…"'],
    photoNo: ['"Scusi, I\'m in a hurry."', '"No, grazie."', 'They pretend not to hear you.', '"Ask someone else, sorry."'],
    friendText: ['Ciao! Haven\'t seen you in ages.', 'Aperitivo this weekend?', 'I saw something you\'d like.'],
    commentFamily: ['Bellissimo! Un bacio ❤️', 'Where is this? Call your mother.', 'You look like your father here 😂', 'Are you eating properly?'],
    commentFriend: ['Che figo 🔥', 'Stupendo!!', 'Where is this?? 😍', 'Che classe', 'Send me this pic', 'Mi manchi'],
    commentStranger: ['Bellissima 👌', 'Firenze ❤️', 'This light is everything', 'Follow back?', 'Where is this?', '❤️❤️'],
    commentFlat: ['Carino', '👍', 'Ok'],
    bump: ['Scusi!', 'Ehi! Attenzione!', 'Mamma mia…', 'Permesso!', 'Careful!', 'Ma guarda dove vai!'],
    dropoff: '"Ecco. Buona giornata!"',
    boss: '"Work has been crazy this week. My boss is impossible."',
    quit: '"Just quit. Life is short."',
    shrug: '"Boh, she\'ll be fine."',
    memory: ['"Remember the night we missed the last bus from Fiesole and walked down under the stars?"', '"And you sang the whole way!"'],
    friendsOnly: '"Restiamo amici, va bene?"',
  },
  usa: {
    photoYes: ['"Sure thing. Step back a little."', '"Oh, totally. Say cheese!"', '"Want the whole building in it?"', '"Okay, one, two…"'],
    photoNo: ['"Sorry, I\'m running late."', '"Nah, sorry."', 'They pretend not to hear you.', '"Ask someone else, man."'],
    friendText: ['Hey stranger! It\'s been forever.', 'You around this weekend? Drinks?', 'Saw something that made me think of you.'],
    commentFamily: ['So proud of you sweetie ❤️', 'Where is this?? Call your mom!', 'You look just like your dad here 😂', 'Are you eating enough?'],
    commentFriend: ['Okay I see you 🔥', 'Stunning!!', 'Wait where is this 😍', 'Main character energy', 'Send me this pic', 'Miss you!!'],
    commentStranger: ['Great shot 👌', 'This light is everything', 'Follow back?', 'Where is this?', 'Vibes', '❤️❤️'],
    commentFlat: ['Nice', '👍', 'Cool'],
    bump: ['Whoa! Watch it!', 'Excuse me!', 'Hey, eyes up!', 'Seriously?', 'Careful, buddy!', 'Sorry—oh, never mind.'],
    dropoff: '"Here you go. Have a good one!"',
    boss: '"Work has been insane this week. My manager is on my case."',
    quit: '"Just quit, dude."',
    shrug: '"Eh, she\'ll be fine."',
    memory: ['"Remember that road trip when the car died outside Reno?"', '"And we ate gas-station burritos for two days!"'],
    friendsOnly: '"Let\'s just stay friends, okay?"',
  },
  china: {
    photoYes: ['"好的 (Okay). Stand there."', '"Sure. Smile, say 茄子 (qiézi, \'cheese\')!"', '"Should I get the whole building?"', '"一, 二, 三 (one, two, three)…"'],
    photoNo: ['"不好意思 (sorry), I\'m in a hurry."', '"No, sorry."', 'They pretend not to hear you.', '"Ask someone else."'],
    friendText: ['好久不见 (long time no see)! Where have you been?', 'Hotpot this weekend?', 'Saw something you\'d like.'],
    commentFamily: ['So proud of you ❤️', 'Where is this? Wear more clothes, it\'s cold!', 'Have you eaten?', 'Call your grandmother.'],
    commentFriend: ['太美了 (so beautiful) 🔥', '绝了 (unreal)!!', 'Where is this?? 😍', 'Send me this pic', 'Take me next time!'],
    commentStranger: ['Nice shot 👌', '好看 (looks great)', 'Where is this?', 'Follow back?', '❤️❤️'],
    commentFlat: ['不错 (not bad)', '👍', 'Ok'],
    bump: ['哎! Watch where you\'re going!', '不好意思 (excuse me)!', 'Hey! Careful!', '看路啊 (look at the road)!', 'Careful!'],
    dropoff: '"到了 (we\'re here). Please rate me five stars!"',
    boss: '"Work has been crazy this week. 996 again. My boss is on my neck."',
    quit: '"Just quit. 躺平 (lie flat)."',
    shrug: '"Ah, she\'ll be fine."',
    memory: ['"Remember the night before the gaokao, when we sneaked out for skewers?"', '"And your mum caught us on the way back!"'],
    friendsOnly: '"Let\'s just stay friends, okay?"',
  },
  japan: {
    photoYes: ['"はい (yes), of course. Stand there."', '"Sure. はい、チーズ (say cheese)!"', '"Should I get the whole street?"', '"One more, just in case."'],
    photoNo: ['"すみません (sorry), I\'m in a hurry."', 'A small bow, and they hurry on.', 'They pretend not to hear you.', '"Sorry, I can\'t."'],
    friendText: ['久しぶり (long time)! How are you?', 'Izakaya this weekend?', 'Saw something you\'d like.'],
    commentFamily: ['Looking well ❤️', 'Where is this? Take care of yourself.', 'Are you eating properly?', 'Come home for Obon.'],
    commentFriend: ['すごい (amazing) 🔥', 'Beautiful!!', 'Where is this?? 😍', 'Send me this pic', 'Take me next time!'],
    commentStranger: ['Nice shot 👌', 'きれい (beautiful)', 'Where is this?', 'Follow back?', '❤️❤️'],
    commentFlat: ['いいね (nice)', '👍', 'Ok'],
    bump: ['すみません (sorry)!', 'あっ… (ah…)', 'A quick bow, and they\'re gone.', 'Careful!', 'Excuse me!'],
    dropoff: '"ありがとうございました (thank you very much)." The door swings open by itself.',
    boss: '"Work has been crazy this week. Overtime every night, and the boss wants nomikai on Friday."',
    quit: '"Just quit. Seriously."',
    shrug: '"Ah, she\'ll be fine."',
    memory: ['"Remember the school trip to Kyoto, when we got lost in Fushimi Inari?"', '"And you bought a fox mask to say sorry to the teacher!"'],
    friendsOnly: '"Let\'s just stay friends, okay?"',
  },
};

export const voiceOf = (c: Country): Voice => VOICES[c];
